export const LIP_SYNC_CONTRACT_VERSION = 1;
export const SDK_PLATFORMS = ["html", "webApp", "macOS", "android", "iOS"];
export const SHIPPING_RESOLUTIONS = ["210x280", "360x480"];
export const FREE_PLAN_WATERMARK = Object.freeze({
    text: "EXPERTS ALIVE",
    scale: 1.5,
    bottomInset: 12,
    fontSize: 13.5,
    dotSize: 10.5,
    gap: 10.5,
    horizontalPadding: 16.5,
    verticalPadding: 10.5,
    borderWidth: 1.5,
    dotRingWidth: 4.5,
});
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function requireFiniteNumber(value, label) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${label} must be a finite number.`);
    }
}
export function assertApprovedLipSyncSettings(value) {
    if (!isRecord(value) || value.contractVersion !== LIP_SYNC_CONTRACT_VERSION) {
        throw new Error("Unsupported lip-sync settings contract.");
    }
    if (!Array.isArray(value.languageProfiles) || value.languageProfiles.length !== 57) {
        throw new Error("The initial SDK release requires exactly 57 language profiles.");
    }
    for (const profile of value.languageProfiles) {
        if (!isRecord(profile) || profile.approved !== true || typeof profile.locale !== "string") {
            throw new Error("Every language profile must be approved and include a locale.");
        }
        requireFiniteNumber(profile.motionRate, `${profile.locale}.motionRate`);
        requireFiniteNumber(profile.voiceRate, `${profile.locale}.voiceRate`);
        requireFiniteNumber(profile.motionStartSeconds, `${profile.locale}.motionStartSeconds`);
        requireFiniteNumber(profile.motionEndSeconds, `${profile.locale}.motionEndSeconds`);
    }
    if (!Array.isArray(value.platformProfiles)) {
        throw new Error("Platform volume profiles are required.");
    }
    for (const platform of SDK_PLATFORMS) {
        if (!value.platformProfiles.some((profile) => isRecord(profile) && profile.platform === platform)) {
            throw new Error(`Missing ${platform} volume profile.`);
        }
    }
    const html = value.platformProfiles.find((profile) => isRecord(profile) && profile.platform === "html");
    if (!isRecord(html) || html.volumeScale !== 0.6 || html.approved !== true) {
        throw new Error("The approved HTML volume profile must be 60%.");
    }
}
function assertMediaAsset(value, label) {
    if (!isRecord(value) || typeof value.path !== "string" || typeof value.sha256 !== "string") {
        throw new Error(`${label} is missing a path or SHA-256 checksum.`);
    }
    if (!/^[a-f0-9]{64}$/i.test(value.sha256))
        throw new Error(`${label} has an invalid SHA-256 checksum.`);
    requireFiniteNumber(value.bytes, `${label}.bytes`);
    if (value.bytes <= 0)
        throw new Error(`${label}.bytes must be positive.`);
}
export function assertDigitalHumanAssetManifest(value) {
    if (!isRecord(value) || value.contractVersion !== LIP_SYNC_CONTRACT_VERSION || value.schemaVersion !== 1) {
        throw new Error("Unsupported Digital Human asset manifest.");
    }
    if (value.processing !== "local" || value.checksumAlgorithm !== "SHA-256") {
        throw new Error("The Web SDK only accepts local-processing SHA-256 manifests.");
    }
    if (typeof value.manifestVersion !== "string" || typeof value.digitalHumanId !== "string") {
        throw new Error("The asset manifest requires manifestVersion and digitalHumanId.");
    }
    if (value.mode !== "natural-speech-motion-v1" && value.mode !== "continuous-speech-motion-v1") {
        throw new Error("The asset manifest uses an unsupported motion mode.");
    }
    if (value.transitionAnchorSeconds !== undefined) {
        requireFiniteNumber(value.transitionAnchorSeconds, "transitionAnchorSeconds");
        requireFiniteNumber(value.speakingDurationSeconds, "speakingDurationSeconds");
        if (value.transitionAnchorSeconds < 0
            || value.transitionAnchorSeconds >= value.speakingDurationSeconds) {
            throw new Error("transitionAnchorSeconds must fall inside the speaking clip.");
        }
    }
    if (value.mode === "continuous-speech-motion-v1") {
        if (!isRecord(value.continuousMotion)) {
            throw new Error("A continuous-motion manifest requires a frame index.");
        }
        assertMediaAsset(value.continuousMotion.frameIndex, "continuousMotion.frameIndex");
        requireFiniteNumber(value.continuousMotion.framesPerSecond, "continuousMotion.framesPerSecond");
        if (value.continuousMotion.framesPerSecond < 12
            || value.continuousMotion.framesPerSecond > 120
            || value.continuousMotion.guideVersion !== 1) {
            throw new Error("The continuous-motion frame guide is unsupported.");
        }
    }
    else if (value.continuousMotion !== undefined) {
        throw new Error("A frame index cannot be attached to a legacy motion manifest.");
    }
    if (!isRecord(value.variants))
        throw new Error("The asset manifest has no variants.");
    for (const resolution of SHIPPING_RESOLUTIONS) {
        const variant = value.variants[resolution];
        if (!isRecord(variant))
            throw new Error(`Missing required ${resolution} motion variant.`);
        assertMediaAsset(variant.carrier, `${resolution}.carrier`);
        assertMediaAsset(variant.speaking, `${resolution}.speaking`);
        assertMediaAsset(variant.poster, `${resolution}.poster`);
    }
}
export function assertDigitalHumanCatalog(value) {
    if (!isRecord(value) || value.contractVersion !== LIP_SYNC_CONTRACT_VERSION || value.schemaVersion !== 1) {
        throw new Error("Unsupported Digital Human catalog.");
    }
    if (value.processing !== "local" || value.downloadScope !== "selected-human-only" || value.checksumAlgorithm !== "SHA-256") {
        throw new Error("The Digital Human catalog does not match the local SDK contract.");
    }
    if (typeof value.catalogVersion !== "string" || value.catalogVersion.length === 0) {
        throw new Error("The Digital Human catalog requires a version.");
    }
    const shippingResolutions = value.shippingResolutions;
    if (!Array.isArray(shippingResolutions)
        || !SHIPPING_RESOLUTIONS.every((resolution) => shippingResolutions.includes(resolution))) {
        throw new Error("The Digital Human catalog is missing a shipping resolution.");
    }
    if (!Array.isArray(value.referenceOnlyResolutions) || !value.referenceOnlyResolutions.includes("720x960")) {
        throw new Error("The Digital Human catalog must keep 720x960 reference-only.");
    }
    if (!Array.isArray(value.humans) || value.humans.length === 0) {
        throw new Error("The Digital Human catalog is empty.");
    }
    const ids = new Set();
    const slugs = new Set();
    const supportedModes = new Set(["natural-speech-motion-v1", "continuous-speech-motion-v1"]);
    const allowedVoices = new Set([
        "alloy", "ash", "cedar", "coral", "echo", "marin", "sage", "shimmer", "verse",
    ]);
    for (const human of value.humans) {
        const resolutions = isRecord(human) ? human.resolutions : null;
        if (!isRecord(human)
            || typeof human.id !== "string"
            || typeof human.slug !== "string"
            || typeof human.defaultLocale !== "string"
            || typeof human.manifestUrl !== "string"
            || typeof human.mode !== "string"
            || !supportedModes.has(human.mode)
            || !Array.isArray(resolutions)
            || !SHIPPING_RESOLUTIONS.every((resolution) => resolutions.includes(resolution))) {
            throw new Error("The Digital Human catalog contains an invalid entry.");
        }
        if (ids.has(human.id) || slugs.has(human.slug)) {
            throw new Error("The Digital Human catalog contains duplicate IDs or slugs.");
        }
        ids.add(human.id);
        slugs.add(human.slug);
    }
    if (value.releaseChannel === "web-production") {
        const platforms = Array.isArray(value.platforms) ? new Set(value.platforms) : new Set();
        if (platforms.size !== 3
            || !["html", "web", "react"].every((platform) => platforms.has(platform))) {
            throw new Error("The web production catalog has an invalid platform declaration.");
        }
        if (!value.humans.every((human) => isRecord(human)
            && typeof human.voice === "string"
            && allowedVoices.has(human.voice))) {
            throw new Error("The web production catalog is missing a frozen Realtime voice.");
        }
    }
}
export function resolveResolutionEntitlement(plan, bandwidthSaver = false) {
    if (plan === "free") {
        return { plan, requestedBandwidthSaver: bandwidthSaver, resolution: "210x280" };
    }
    return {
        plan,
        requestedBandwidthSaver: bandwidthSaver,
        resolution: bandwidthSaver ? "210x280" : "360x480",
    };
}
export function selectLanguageProfile(settings, requestedLocale) {
    const locale = requestedLocale.trim().toLowerCase();
    const exact = settings.languageProfiles.find((profile) => profile.locale.toLowerCase() === locale);
    if (exact)
        return exact;
    const language = locale.split("-")[0];
    const sameLanguage = settings.languageProfiles.find((profile) => profile.locale.toLowerCase().split("-")[0] === language);
    return sameLanguage
        ?? settings.languageProfiles.find((profile) => profile.locale === "en-US")
        ?? settings.languageProfiles[0];
}
export function selectPlatformVolume(settings, platform) {
    const profile = settings.platformProfiles.find((candidate) => candidate.platform === platform);
    if (!profile)
        throw new Error(`No volume profile exists for ${platform}.`);
    return profile;
}
